/** @format */
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';

import { SearchFacet } from '@toucan/common/services/search/constants';

import { setSearchParamsForSearchResults } from '@toucan/client/global/nextjs';

import { getFiltersToRemove } from '@toucan/client/features/search/components/model-search-results/helpers/filters';
import { useSearchParams } from '@toucan/client/features/search/components/model-search-results/hooks/use-search-params';
import {
    Filter,
    FilterOption,
} from '@toucan/client/features/search/components/model-search-results/types';

import {
    getFiltersThatHaveBeenCleared,
    getMultipleSelectedFiltersFromQueryString,
    transformClearedFiltersToSearchParams,
    transformSelectedFiltersToSearchParams,
} from './helpers';

export const useSelectedItems = (
    filters: Filter[],
    onMenuClose?: () => void
) => {
    const router = useRouter();
    const { selectedFilters: selectedFiltersFromSearchParams } =
        useSearchParams();

    const selectedFiltersFromQueryString = useMemo(
        () =>
            getMultipleSelectedFiltersFromQueryString(
                filters,
                selectedFiltersFromSearchParams
            ),
        [filters, selectedFiltersFromSearchParams]
    );

    const [selectedFilters, setSelectedFilters] = useState<
        Record<SearchFacet, Filter>
    >(selectedFiltersFromQueryString);

    const hasSelectedFilters = useMemo(() => {
        return Object.values(selectedFilters ?? {}).length !== 0;
    }, [selectedFilters]);

    function handleApply(): void {
        const searchParams =
            transformSelectedFiltersToSearchParams(selectedFilters);

        const clearedFilters = getFiltersThatHaveBeenCleared(
            selectedFiltersFromSearchParams,
            searchParams
        );

        const searchParamsForClearedFilters =
            transformClearedFiltersToSearchParams(clearedFilters);

        setSearchParamsForSearchResults(router, {
            ...searchParams,
            ...searchParamsForClearedFilters,
            page: undefined,
        });
        onMenuClose?.();
    }

    function handleFilterUpdate(
        filter: Filter,
        selectedItems: FilterOption[]
    ): void {
        setSelectedFilters((prevSelectedFilters) => {
            return {
                ...prevSelectedFilters,
                [filter.id]: {
                    ...filter,
                    values: selectedItems,
                },
            };
        });
    }

    function handleClearAll(): void {
        setSelectedFilters({} as Record<SearchFacet, Filter>);

        const activeFilters = Object.values(selectedFilters);
        const filtersToRemove = getFiltersToRemove(activeFilters);

        setSearchParamsForSearchResults(router, {
            ...filtersToRemove,
            page: undefined,
        });
        onMenuClose?.();
    }

    function onResetAndClose(): void {
        setSelectedFilters(selectedFiltersFromQueryString);
        onMenuClose?.();
    }

    function handleFilterClear(filterId: SearchFacet): void {
        setSelectedFilters((prevSelectedFilters) => {
            const updatedSelectedFilters = { ...prevSelectedFilters };
            delete updatedSelectedFilters[filterId];

            return updatedSelectedFilters;
        });
    }

    return {
        selectedFilters,
        hasSelectedFilters,
        handleFilterUpdate,
        onResetAndClose,
        handleFilterClear,
        handleApply,
        handleClearAll,
    };
};
